import { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { ALLOWED_HOSTS } from '@/components/editor/IframeExtension';

// A17: el contenido de news_articles lo genera un LLM a partir de tendencias web
// scrapeadas (origen NO confiable). DOMPurify permitía <iframe> con src arbitrario y
// sin sandbox → clickjacking / phishing con el look&feel de la app. Aquí se reutiliza
// la allowlist de hosts del editor (youtube/vimeo/canva): cualquier iframe de otro
// host se elimina, y a los permitidos se les fuerza sandbox restrictivo.
function sanitizeNewsHtml(html: string): string {
  DOMPurify.addHook('afterSanitizeAttributes', (node: any) => {
    if (node.tagName === 'IFRAME') {
      let ok = false;
      try { ok = ALLOWED_HOSTS.includes(new URL(node.getAttribute('src') || '').hostname); } catch { ok = false; }
      if (!ok) { node.remove(); return; }
      node.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-presentation');
      node.setAttribute('referrerpolicy', 'no-referrer');
    }
  });
  try {
    return DOMPurify.sanitize(html, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'src', 'width', 'height', 'title', 'referrerpolicy', 'sandbox'],
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
}
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Newspaper,
  Search,
  Clock,
  ArrowRight,
  Sparkles,
  Loader2,
  Eye,
  Share2,
  Link as LinkIcon,
  Linkedin,
  Twitter,
  Facebook,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNewsArticles, useIncrementNewsViews, type NewsArticle } from '@/hooks/useNewsArticles';
import { useNewsCategories, type NewsCategory } from '@/hooks/useNewsCategories';
import { SEO } from '@/components/SEO';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

// Fallback images
import aiFutureImg from '@/assets/news/ai-future.jpg';
import leadershipImg from '@/assets/news/leadership.jpg';
import latamAutomationImg from '@/assets/news/latam-automation.jpg';
import promptingImg from '@/assets/news/prompting.jpg';
import dataWorkshopImg from '@/assets/news/data-workshop.jpg';

const fallbackImages = [aiFutureImg, leadershipImg, latamAutomationImg, promptingImg, dataWorkshopImg];

const badgeColors = [
  'bg-violet-500/20 text-violet-700 dark:text-violet-300',
  'bg-blue-500/20 text-blue-700 dark:text-blue-300',
  'bg-green-500/20 text-green-700 dark:text-green-300',
  'bg-orange-500/20 text-orange-700 dark:text-orange-300',
  'bg-pink-500/20 text-pink-700 dark:text-pink-300',
  'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300',
  'bg-amber-500/20 text-amber-700 dark:text-amber-300',
  'bg-red-500/20 text-red-700 dark:text-red-300',
  'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300',
];

const getCategoryBadge = (article: NewsArticle, categories: NewsCategory[]) => {
  const cat = categories.find(c => c.id === article.category_id);
  const label = cat?.name ?? 'General';
  const idx = cat ? categories.indexOf(cat) : 0;
  const colorClass = badgeColors[idx % badgeColors.length];
  return (
    <Badge className={colorClass}>
      <Sparkles className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

const NewsPage = () => {
  const { articleId } = useParams<{ articleId?: string }>();
  const navigate = useNavigate();
  const { data: articles = [], isLoading, isError: articlesError } = useNewsArticles();
  const { data: categories = [] } = useNewsCategories();
  const incrementViews = useIncrementNewsViews();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNews, setSelectedNews] = useState<NewsArticle | null>(null);
  const [checkingCert, setCheckingCert] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Sync URL param with selected article
  const viewedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!articleId) {
      setSelectedNews(null);
      setCheckingCert(false);
      return;
    }

    if (isLoading) return;

    const found = articles.find(a => a.id === articleId);
    if (found) {
      setSelectedNews(found);
      setCheckingCert(false);
      if (!viewedRef.current.has(articleId)) {
        viewedRef.current.add(articleId);
        incrementViews.mutate(articleId);
      }
      return;
    }

    // No es article. Verificar si es un cert (redirect del proxy
    // de lideria.link envía humanos a /news/{uuid} sin importar
    // el tipo real). Si es cert, redirigir a /c/{id}.
    setSelectedNews(null);
    setCheckingCert(true);
    let cancelled = false;
    (async () => {
      try {
        console.log('[cert-redirect] checking uuid:', articleId);
        const { data, error } = await supabase.rpc('get_cert_og_data', {
          _cert_id: articleId,
        });
        console.log('[cert-redirect] rpc result:', { data, error });

        if (cancelled) return;
        if (error) {
          console.error('[cert-redirect] RPC error:', error);
          setCheckingCert(false);
          navigate('/news', { replace: true });
          return;
        }

        const row = Array.isArray(data) ? data[0] : data;
        if (row) {
          setCheckingCert(false);
          navigate(`/c/${articleId}`, { replace: true });
        } else {
          // Ni article ni cert — limpiar URL y mostrar listado.
          setCheckingCert(false);
          navigate('/news', { replace: true });
        }
      } catch (error) {
        console.error('[cert-redirect] exception:', error);
        if (!cancelled) {
          setCheckingCert(false);
          navigate('/news', { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId, articles, isLoading]);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const itemsPerPage = 24;

  const buildShareUrl = (id: string) => `https://lideria.link/${id}`;

  const handleShareWhatsApp = () => {
    if (!selectedNews) return;
    const url = buildShareUrl(selectedNews.id);
    const text = `${selectedNews.title} ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShareSocial = (network: 'linkedin' | 'twitter' | 'facebook') => {
    if (!selectedNews) return;
    const url = buildShareUrl(selectedNews.id);
    const title = selectedNews.title;
    const shareUrls: Record<typeof network, string> = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    window.open(shareUrls[network], '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = async () => {
    if (!selectedNews) return;
    const url = buildShareUrl(selectedNews.id);
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copiado', description: url });
    } catch {
      toast({ title: 'No se pudo copiar', variant: 'destructive' });
    }
  };


  const getImage = (article: NewsArticle, index: number) => {
    return article.image_url || fallbackImages[index % fallbackImages.length];
  };

  const featuredNews = articles.filter(n => n.featured);

  const filteredNews = articles.filter(news => {
    if (news.featured) return false;
    const matchesSearch = news.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (news.excerpt ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || news.category_id === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const nonFeaturedNews = filteredNews;
  const totalPages = Math.ceil(nonFeaturedNews.length / itemsPerPage);
  const paginatedNews = nonFeaturedNews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleCategoryChange = (category: string) => {
    setCategoryFilter(category);
    setCurrentPage(1);
  };

  const renderNewsCard = (news: NewsArticle, index: number, featured = false) => (
    <Card 
      key={news.id} 
      className={`cursor-pointer transition-all hover:shadow-md overflow-hidden ${featured ? 'border-primary/30 bg-primary/5' : ''}`}
      onClick={() => navigate(`/news/${news.id}`)}
    >
      <div className={`relative overflow-hidden ${featured ? 'h-48' : 'h-36'}`}>
        <img 
          src={getImage(news, index)} 
          alt={news.title}
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
        />
        {news.featured && (
          <div className="absolute top-2 left-2">
            <Badge variant="outline" className="border-amber-500 text-amber-600 bg-background/80 backdrop-blur-sm">
              Destacado
            </Badge>
          </div>
        )}
      </div>
      
      <CardHeader className="pb-2 pt-3">
        <div className="flex items-center gap-2 mb-1">
          {getCategoryBadge(news, categories)}
        </div>
        <CardTitle className={`${featured ? 'text-xl' : 'text-lg'} line-clamp-2`}>
          {news.title}
        </CardTitle>
        <CardDescription className="line-clamp-2">
          {news.excerpt}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm text-muted-foreground flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span>{news.author}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {news.read_time ?? 5} min
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {news.views ?? 0}
            </span>
          </div>
          {news.published_at && (
            <span>{format(parseISO(news.published_at), "d MMM", { locale: es })}</span>
          )}
        </div>
        <Button variant="link" className="p-0 h-auto mt-2 text-primary">
          Leer más <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );

  if (selectedNews) {
    const idx = articles.findIndex(a => a.id === selectedNews.id);
    const relatedNews = articles
      .filter(a => a.id !== selectedNews.id)
      .slice(0, 3);

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/news')}
          >
            ← Volver a noticias
          </Button>
        </div>


        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main article */}
          <article className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {getCategoryBadge(selectedNews, categories)}
            </div>

            <h1 className="text-3xl font-bold mb-4">{selectedNews.title}</h1>

            <div className="flex items-center gap-3 flex-wrap text-muted-foreground mb-6">
              <span>{selectedNews.author}</span>
              {selectedNews.published_at && (
                <span>{format(parseISO(selectedNews.published_at), "d 'de' MMMM, yyyy", { locale: es })}</span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {selectedNews.read_time ?? 5} min de lectura
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {selectedNews.views ?? 0} visitas
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={handleShareWhatsApp}>
                  <Share2 className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <Button variant="outline" size="sm" onClick={handleCopyLink}>
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Copiar link
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Share2 className="w-4 h-4 mr-2" />
                      Redes sociales
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleShareSocial('linkedin')}>
                      <Linkedin className="w-4 h-4 mr-2" />
                      LinkedIn
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShareSocial('twitter')}>
                      <Twitter className="w-4 h-4 mr-2" />
                      X (Twitter)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShareSocial('facebook')}>
                      <Facebook className="w-4 h-4 mr-2" />
                      Facebook
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="relative rounded-lg overflow-hidden mb-6 h-64 md:h-80">
              <img 
                src={getImage(selectedNews, idx)} 
                alt={selectedNews.title}
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <p className="text-lg text-muted-foreground mb-6">{selectedNews.excerpt}</p>
              {selectedNews.content && (
                <div
                  className="[&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-lg [&_iframe]:my-4 [&_iframe]:h-auto"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeNewsHtml(selectedNews.content),
                  }}
                />
              )}
            </div>
          </article>

          {/* Sidebar - related articles */}
          {relatedNews.length > 0 && (
            <aside className="lg:w-80 shrink-0 space-y-4">
              <h3 className="text-lg font-semibold">Más noticias</h3>
              {relatedNews.map((news, i) => {
                const relIdx = articles.findIndex(a => a.id === news.id);
                return (
                  <Card
                    key={news.id}
                    className="cursor-pointer transition-all hover:shadow-md overflow-hidden"
                    onClick={() => navigate(`/news/${news.id}`)}
                  >
                    <div className="relative overflow-hidden h-32">
                      <img
                        src={getImage(news, relIdx)}
                        alt={news.title}
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                    <CardHeader className="pb-2 pt-3">
                      <div className="flex items-center gap-2 mb-1">
                        {getCategoryBadge(news, categories)}
                      </div>
                      <CardTitle className="text-sm line-clamp-2">{news.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {news.read_time ?? 5} min
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {news.views ?? 0}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </aside>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Si venimos con articleId (por proxy 301 desde lideria.link) pero
  // aún no sabemos si es article o cert, mostrar spinner en vez del
  // listado para evitar flash.
  if (articleId && !selectedNews && (checkingCert || isLoading || articlesError || articles.length === 0)) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const seoArticle = selectedNews
    ? {
        title: `${selectedNews.title} — LÍDER IA`,
        description: ((selectedNews as any).summary || (selectedNews as any).excerpt || selectedNews.title || '').toString().replace(/<[^>]+>/g, '').slice(0, 160),
        path: `/news/${selectedNews.id}`,
        ogType: 'article' as const,
        image: selectedNews.image_url || undefined,
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: selectedNews.title,
          image: selectedNews.image_url ? [selectedNews.image_url] : undefined,
          datePublished: (selectedNews as any).published_at || (selectedNews as any).created_at,
          author: { '@type': 'Organization', name: 'LÍDER IA' },
          publisher: { '@type': 'Organization', name: 'LÍDER IA' },
        },
      }
    : {
        title: 'Noticias y tendencias de IA — LÍDER IA',
        description: 'Artículos sobre IA, liderazgo y tendencias para profesionales y empresas en Latinoamérica.',
        path: '/news',
        ogType: 'website' as const,
      };

  return (
    <div className="space-y-6">
      <SEO {...seoArticle} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Newspaper className="w-8 h-8" />
            Noticias
          </h1>
          <p className="text-muted-foreground mt-1">
            Artículos sobre IA, liderazgo y tendencias
          </p>
        </div>
      </div>

      {/* Featured News */}
      {featuredNews.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {featuredNews.map((news, i) => renderNewsCard(news, i, true))}
        </div>
      )}

      {/* Search and Category Filters */}
      <div className="flex flex-col gap-4">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5 pointer-events-none" />
          <Input
            placeholder="Buscar noticias..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-11 h-12 text-base w-full"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant={categoryFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleCategoryChange('all')}
          >
            Todas
          </Button>
          {categories.map((cat, idx) => (
            <Button
              key={cat.id}
              variant={categoryFilter === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleCategoryChange(cat.id)}
            >
              {cat.name}
            </Button>
          ))}
        </div>
      </div>

      {/* News Grid */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedNews.map((news, i) => renderNewsCard(news, i))}
        </div>
        
        {nonFeaturedNews.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No se encontraron noticias
            </CardContent>
          </Card>
        )}
        
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => setCurrentPage(page)}
                    isActive={currentPage === page}
                    className="cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
